'use client';

/*
  Cloudinary already resizes, re-encodes and serves these images from its CDN,
  and the `srcSet` below picks the width. Routing them through `next/image`
  would put a second optimiser in front of that for no gain, so the plain
  element is deliberate.
*/
/* eslint-disable @next/next/no-img-element */
import { limitFit, pad } from '@cloudinary/url-gen/actions/resize';
import { Background } from '@cloudinary/url-gen/qualifiers/background';
import { Color } from '@cloudinary/url-gen/qualifiers/color';
import { FullScreenImageViewer } from 'components/full-screen-image-viewer/full-screen-image-viewer';
import { CheckCircleIcon } from 'components/icons';
import { ListingTypePill } from 'components/listing-type-pill/listing-type-pill';
import { getPlaceholderImage } from 'components/public-listing-card';
import {
  Box,
  Button,
  DividerItem,
  Flex,
  Grid,
  Icon,
  Stack,
  Tag,
  Text,
} from 'components/ui';
import UpdateListingVisibilityDialog from 'components/update-listing-visibility-dialog/update-listing-visibility-dialog';
import { format } from 'date-fns';
import { useBuyerOrg } from 'hooks/use-buyer-org';
import { useDialogToggle } from 'hooks/use-dialog-toggle';
import {
  ListingMedia,
  ListingRead,
  ListingTypeEnum,
  PublicStatusEnum,
  VisibilityEnum,
} from 'lib/api/generated';
import {
  formatAUD,
  formatListingDateRange,
  getListingStatusLabel,
  getTagStatus,
  getTagStatusBackgroundColor,
  getTagStatusColor,
  snakeCaseToTitleCase,
} from 'lib/helpers';
import { getListOfNetworks } from 'lib/marketplace-utils';
// @ts-ignore
import {
  CARD_IMAGE_WIDTHS,
  DETAIL_MEDIA_SIZES,
  SELLER_LOGO_SIZES,
  SELLER_LOGO_WIDTHS,
  buildResponsiveImage,
} from 'lib/cloudinary-image';
import Linkify from 'linkify-react';
import { useRouter } from 'next/router';
import { FC } from 'react';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { useMedia } from 'react-use';
import { DATE_FORMAT, PATHS, cld } from 'settings/config';
import styled, { css } from 'styled-components';
import { breakpoints } from '../../styles/breakpoints';
import { globalTheme } from '../../styles/global-theme';
import { FileChip } from '../file-chip';
import { ExpectedWeight } from '../ui/listing-fields/expected-weight';
import EOIList from './eoi-list';
import ListingAnalyticsInfo from './listing-analytics-info';
import SoldBanner from './sold-banner';

const HeroContent = styled.div<{ isMobile: boolean }>`
  display: grid;
  gap: 30px;
  padding: 60px;
  background-color: ${globalTheme.colors.background};
  border-radius: ${globalTheme.space[0]};
  grid-template-columns: 1fr minmax(300px, 500px);

  @media screen and (min-width: 1600px) {
    grid-template-columns: 8fr minmax(600px, 4fr);
  }

  @media screen and (min-width: 800px) and (max-width: 1200px) {
    display: flex;
    flex-direction: column-reverse;
    gap: 20px;
    padding: 20px;
  }

  @media screen and (max-width: 800px) {
    display: flex;
    flex-direction: column-reverse;
    gap: 15px;
    padding: 20px;
  }
`;

export const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${globalTheme.space[0]};
  margin-bottom: ${globalTheme.space[1]};
`;

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: ${globalTheme.space[2]};
  gap: ${globalTheme.space[1]};
  border-top: 1px solid ${globalTheme.colors.darkGrey};
  border-bottom: 1px solid ${globalTheme.colors.darkGrey};
  margin: ${globalTheme.space[1]} 0;
  @media screen and (max-width: ${breakpoints.small}) {
    flex-direction: column;
    align-items: flex-start;
    padding-left: 0;
    ${Button} {
      align-self: center;
      font-size: 20px;
      padding: ${globalTheme.space[2]};
    }
  }
`;

const ListedList = styled.ul`
  margin-left: ${globalTheme.space[3]};
`;

const ImageWrapper = styled.div<{ isMobile: boolean; hasTopMargin?: boolean }>`
  display: ${(props) => (props.isMobile ? 'none' : 'block')};
  width: 160px;
  height: 130px;
  margin: ${(props) => props.isMobile && '0 auto'};
  margin-top: ${(props) => (props.hasTopMargin ? globalTheme.space[2] : '')};
  border-radius: ${globalTheme.space[2]};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const ActionButton = styled(Button)<{ soldBtn?: boolean }>`
  background-color: ${(props) =>
    props.soldBtn ? globalTheme.colors.darkRed : ''};
`;

const ActionCTAButton = styled(Button)`
  width: 100%;
  max-width: 200px;
  height: 44px;
`;

const CompanyDetailWrapper = styled.div`
  padding: ${globalTheme.space[1]} 0;
`;
const HeroContentDescription = styled.div<{ isMobile: boolean }>`
  min-width: 0;
`;
const ImageContainer = styled.div`
  display: flex;
  flex-basis: 50%;
  flex-direction: column;
  gap: ${globalTheme.space[2]};

  /* The srcSet candidate the browser picks can be far wider than the column,
     so size it in CSS rather than letting the image lay out at its intrinsic
     pixel width. */
  img {
    width: 100%;
    height: auto;
  }

  @media screen and (max-width: ${breakpoints.large}) {
    flex-basis: 100%;
  }
`;

const Divider = styled(DividerItem)`
  display: flex;
  justify-content: space-between;
`;

const DivWrap = styled.div<{ flex?: boolean; isMobile?: boolean }>`
  ${({ isMobile, flex }) =>
    flex &&
    css`
      display: flex;
      align-items: ${isMobile ? 'start' : 'center'};
      flex-direction: ${isMobile ? 'column' : 'row'};
      gap: ${isMobile ? '4px' : globalTheme.space[0]};
      ${globalTheme.space[0]};
    `};
`;

const TextData = styled(Text).attrs({ as: 'span' })<{
  flex?: boolean;
  isMobile?: boolean;
}>`
  font-size: 16px;
  display: block;
  ${({ isMobile, flex }) =>
    flex &&
    css`
      display: flex;
      align-items: ${isMobile ? 'start' : 'center'};
      flex-direction: ${isMobile ? 'column' : 'row'};
      gap: ${isMobile ? '4px' : globalTheme.space[0]};
      ${globalTheme.space[0]};
    `};

  .comment-link-text {
    color: ${globalTheme.colors.blue};
    text-decoration: underline;
    cursor: pointer;
  }
`;

const ActionButtonContainer = styled(Flex)<{ showInMobile?: boolean }>`
  margin: ${globalTheme.space[2]} 0;
  .request-callback-btn {
    background-color: ${globalTheme.colors.pink};
    &:hover {
      background-color: ${globalTheme.colors.lightPink};
      color: black;
    }
  }
  .send-offer-btn {
    background-color: black;
    &:hover {
      background-color: ${globalTheme.colors.stockbookBorder};
      color: black;
    }
  }
  @media screen and (max-width: ${breakpoints.xxLarge}) {
    display: ${({ showInMobile }) => (showInMobile ? 'flex' : 'none')};
  }
`;

const TitleText = styled(Text)`
  font-weight: 500;
  font-size: 16px;
`;

const VerifiedAgentTag = styled(Tag)`
  padding: ${globalTheme.space[1]} 14px;
  color: ${globalTheme.colors.blue};
  background-color: ${globalTheme.colors.lightBlue};
  * {
    fill: ${globalTheme.colors.blue};
  }
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${globalTheme.space[0]};

  @media screen and (max-width: ${breakpoints.medium}) {
    grid-template-columns: 1fr;
  }
`;

const DetailGrid = styled(Grid)`
  @media screen and (max-width: ${breakpoints.medium}) {
    grid-template-columns: 100px 1fr;
  }
`;

const EOIWrapper = styled(Stack)`
  padding-bottom: ${globalTheme.space[2]};
  border-bottom: 1px solid ${globalTheme.colors.darkGrey};
`;

const StatusTag = styled(Tag)`
  padding: ${globalTheme.space[1]} 14px;
  min-width: fit-content;
  align-items: center;
  text-transform: capitalize;
`;

const ArchivedWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${globalTheme.space[0]};
`;

interface ListingsDetailProps {
  listingDetails: ListingRead;
  shareUrl?: string;
  publicListingData?: ListingRead;
  handleShareListing?: VoidFunction;
  refetch?: any;
  handleMarkAsSold?: Function;
  handleSendAction?: (type: 'offer' | 'enquiry' | 'callback') => void;
}

interface MixedBreed {
  name: string;
  composition: string;
}

interface TreatmentType {
  name: string;
  treatment_date: string;
}

function ListingDetails({
  listingDetails,
  publicListingData,
  handleShareListing,
  handleMarkAsSold,
  handleSendAction,
}: ListingsDetailProps) {
  const isMobile = useMedia(`(max-width: ${breakpoints.medium})`, false);
  const router = useRouter();
  // An auction's sold state is time-driven (it closes at 6pm on the auction date), so it can't be
  // set by hand — the API rejects it. It still *displays* as sold once its close has passed.
  const isAuction = listingDetails.price_method === 'AUCTION';

  const [
    isListingVisibilityOpen,
    openListingVisibilityDialog,
    closeListingVisibilityDialog,
  ] = useDialogToggle(false);

  const {
    type,
    title,
    listing_address,
    media,
    sold,
    sold_price,
    sold_price_type,
    sold_hide_price,
    product,
    org,
    last_updated,
    quantity_label,
    form_data,
    visibility,
    public_status,
    prices_label,
    metadata,
    date_from,
    date_to,
  } = listingDetails;

  const { data: buyerOrg } = useBuyerOrg();
  const images = media.filter(
    (media) =>
      (media.resource_type === 'image' || media.resource_type === 'video') &&
      media.format !== 'pdf'
  );

  const files = media.filter(
    (media) => media.resource_type === 'raw' || media.format === 'pdf'
  );

  // Alt-text fallback for listing images, mirroring the page-title pattern:
  //   "<title>, <suburb> <state> - Agora Livestock listing <id>"
  // Suburb/state aren't always present, so the location segment is dropped when
  // both are absent. A media item's own description still takes precedence.
  const altLocation = [listing_address?.suburb, listing_address?.state]
    .filter(Boolean)
    .join(' ');
  const imageAltText = `${[title, altLocation]
    .filter(Boolean)
    .join(', ')} - Agora Livestock listing ${listingDetails.id}`;

  const formattedSoldPriceText = !sold_hide_price ? prices_label : null;

  // An auction's date already shows as "Auction date:" — don't repeat it as a sold date.
  const soldDateLabel =
    sold && !isAuction
      ? `(sold: ${format(new Date(sold), DATE_FORMAT.listing)})`
      : null;

  const soldPriceDetailText = sold
    ? [formattedSoldPriceText, soldDateLabel].filter(Boolean).join(' ')
    : null;

  const getGenderValue = (product: string, pregnancyValue?: string) => {
    if (product === 'GT') {
      const gt_gender: string[] = [];
      Array.isArray(form_data?.goat_gender) && (
        <>
          {form_data?.goat_gender.map((gender: any) => {
            if (gender === 'Doe' && pregnancyValue) {
              gt_gender.push(gender + ` (${pregnancyValue})`);
            } else {
              gt_gender.push(gender);
            }
          })}
        </>
      );

      return gt_gender.length ? gt_gender.join(', ') : '';
    } else if (product === 'SH') {
      const sh_gender: string[] = [];
      Array.isArray(form_data?.sheep_gender) && (
        <>
          {form_data?.sheep_gender.map((gender: any) => {
            if (gender === 'Ewes' && pregnancyValue) {
              sh_gender.push(gender + ` (${pregnancyValue})`);
            } else {
              sh_gender.push(gender);
            }
          })}
        </>
      );

      return sh_gender.length ? sh_gender.join(', ') : '';
    } else if (product === 'CT') {
      const ct_gender: string[] = [];
      Array.isArray(form_data?.cattle_gender) && (
        <>
          {form_data?.cattle_gender.map((gender: any) => {
            if (
              (gender === 'Cows' ||
                gender === 'Cows & calves' ||
                gender === 'Heifers') &&
              pregnancyValue
            ) {
              ct_gender.push(gender + ` (${pregnancyValue})`);
            } else {
              ct_gender.push(gender);
            }
          })}
        </>
      );

      return ct_gender.length ? ct_gender.join(', ') : '';
    }
  };

  const getPregnancyValue = (product: string) => {
    if (product === 'GT' && Array.isArray(form_data?.goat_pregnancy_status)) {
      return form_data?.goat_pregnancy_status?.join(', ');
    } else if (
      product === 'SH' &&
      Array.isArray(form_data?.sheep_pregnancy_status)
    ) {
      return form_data?.sheep_pregnancy_status?.join(', ');
    } else if (
      product === 'CT' &&
      Array.isArray(form_data?.cattle_pregnancy_status)
    ) {
      return form_data?.cattle_pregnancy_status?.join(', ');
    }
  };

  const getStockAgesValue = (product: string) => {
    if (product === 'GT') {
      return (
        Array.isArray(form_data?.goat_stock_ages) &&
        form_data?.goat_stock_ages?.join(', ')
      );
    } else if (product === 'SH') {
      return (
        Array.isArray(form_data?.sheep_stock_ages) &&
        form_data?.sheep_stock_ages?.join(', ')
      );
    } else if (product === 'CT') {
      return (
        Array.isArray(form_data?.cattle_stock_ages) &&
        form_data?.cattle_stock_ages?.join(', ')
      );
    }
  };

  const getStockWeaningDate = (product: string) => {
    if (product === 'GT' && form_data?.goat_weaning_date) {
      return typeof form_data?.goat_weaning_date === 'string'
        ? form_data?.goat_weaning_date
        : form_data?.goat_weaning_date?.join('/');
    } else if (product === 'SH' && form_data?.sheep_weaning_date) {
      return typeof form_data?.sheep_weaning_date === 'string'
        ? form_data?.sheep_weaning_date
        : form_data?.sheep_weaning_date?.join('/');
    } else if (product === 'CT' && form_data?.cattle_weaning_date) {
      return typeof form_data?.cattle_weaning_date === 'string'
        ? form_data?.cattle_weaning_date
        : form_data?.cattle_weaning_date?.join('/');
    }
  };

  const BuyerLogo: FC<{ isMobile: boolean; topMargin?: boolean }> = ({
    isMobile,
    topMargin,
  }) => {
    // Captured out here so the width callback below keeps TypeScript's
    // narrowing of `org.logo`.
    const logoPublicId = org?.logo?.public_id;
    return (
      <>
        {logoPublicId && (
          <ImageWrapper isMobile={isMobile} hasTopMargin={topMargin}>
            {/*
              The box is 160x130 and the logo is letterboxed into it, so each
              candidate keeps that ratio. The browser multiplies 160px by the
              device pixel ratio and picks from the ladder, which is why there
              is no `dpr()` here: that would double the chosen width again.
            */}
            <img
              {...buildResponsiveImage(
                (width) =>
                  cld
                    .image(logoPublicId)
                    .format('auto')
                    .quality('auto')
                    .resize(
                      limitFit()
                        .width(width)
                        .height(Math.round((width * 130) / 160))
                    ),
                SELLER_LOGO_WIDTHS
              )}
              sizes={SELLER_LOGO_SIZES}
              alt={`${org?.name || 'Seller'} logo`}
            />
          </ImageWrapper>
        )}
      </>
    );
  };

  const getAgents = () => {
    let agent: string | Element = '';
    if (form_data?.agent === 'no') {
      return 'I am not using an agent';
    }
    if (form_data?.agent === 'agent' || form_data?.agent === 'is_agent') {
      return (
        <>
          <TextData>
            {form_data?.agent === 'agent'
              ? 'I am using an agent'
              : 'I am an agent'}
          </TextData>

          <TextData>
            {[
              form_data?.agent_name,
              form_data?.agent_branch,
              form_data?.agent_agency,
            ]
              .filter(Boolean)
              .join(' - ')}
          </TextData>
        </>
      );
    }
    return agent;
  };

  const pregnancy_values = getPregnancyValue(product);

  return (
    <>
      <SoldBanner
        listingDetails={listingDetails}
        publicListingData={publicListingData}
      />
      <HeroContent isMobile={isMobile}>
        <HeroContentDescription isMobile={isMobile}>
          {isMobile &&
            (metadata.is_owner ||
              (buyerOrg?.is_admin && metadata?.is_org_member)) && (
              <>
                <Grid gap={0} style={{ margin: `${globalTheme.space[1]} 0` }}>
                  {(listingDetails.sold_price_type || listingDetails.sold) && (
                    <ActionButton
                      size='medium'
                      soldBtn={true}
                      fontSize={3}
                      // A sold auction still shows its status, but can't be changed by hand.
                      onClick={
                        isAuction ? undefined : () => handleMarkAsSold?.()
                      }
                    >
                      {getListingStatusLabel(type)}
                      {listingDetails.sold_price && (
                        <> ({formatAUD(listingDetails.sold_price)})</>
                      )}
                    </ActionButton>
                  )}
                  {!isAuction &&
                    (!listingDetails.sold_price_type ||
                      !listingDetails.sold) && (
                      <ActionButton
                        size='medium'
                        soldBtn={true}
                        fontSize={3}
                        onClick={() => handleMarkAsSold?.()}
                      >
                        Mark as {getListingStatusLabel(type)}
                      </ActionButton>
                    )}
                  <Grid
                    columns={'max-content max-content'}
                    gap={0}
                    justifyContent='center'
                  >
                    <ActionButton
                      size='medium'
                      onClick={() =>
                        router.push(
                          `${PATHS.marketplace.editListing}/${listingDetails.id}?type=${listingDetails.type}`
                        )
                      }
                      fontSize={4}
                      iconLeft='EditIcon'
                      style={{ maxWidth: 'max-content' }}
                    >
                      Edit
                    </ActionButton>

                    {visibility !== VisibilityEnum.Hidden &&
                      visibility !== VisibilityEnum.Expired &&
                      visibility !== VisibilityEnum.Archived && (
                        <ActionButton
                          size='medium'
                          variant='outline'
                          fontSize={5}
                          iconLeft='PaperAirplaneIcon'
                          onClick={() => handleShareListing?.()}
                          style={{ maxWidth: 'max-content' }}
                        >
                          Share
                        </ActionButton>
                      )}
                  </Grid>
                </Grid>

                <Flex alignItems='center' gap={0}>
                  {visibility !== VisibilityEnum.Archived &&
                    visibility !== VisibilityEnum.Expired &&
                    public_status !== PublicStatusEnum.Draft && (
                      <StatusTag
                        color={getTagStatusColor(
                          public_status as PublicStatusEnum,
                          visibility
                        )}
                        backgroundColor={getTagStatusBackgroundColor(
                          public_status as PublicStatusEnum,
                          visibility
                        )}
                      >
                        {
                          getTagStatus(
                            public_status as PublicStatusEnum,
                            visibility
                          ).text
                        }
                      </StatusTag>
                    )}
                </Flex>

                {public_status === PublicStatusEnum.Draft &&
                  visibility !== VisibilityEnum.Hidden && (
                    <StatusTag color='white' backgroundColor='red'>
                      <Icon component='LockIcon' />
                      Private
                    </StatusTag>
                  )}

                {visibility === VisibilityEnum.Archived && (
                  <ArchivedWrapper>
                    <StatusTag color='white' backgroundColor='pink'>
                      Archived
                    </StatusTag>
                    <Text>
                      (Archived Reason: {listingDetails.archived_reason})
                    </Text>
                  </ArchivedWrapper>
                )}

                {visibility === VisibilityEnum.Hidden && (
                  <StatusTag
                    fontSize={3}
                    color='black'
                    backgroundColor='yellow'
                  >
                    Draft
                  </StatusTag>
                )}

                {visibility === VisibilityEnum.Expired && (
                  <StatusTag color='darkGrey' backgroundColor='neutral'>
                    Expired
                  </StatusTag>
                )}

                {visibility !== VisibilityEnum.Hidden && (
                  <ActionWrapper>
                    <div>
                      {listingDetails.visibility === VisibilityEnum.Private && (
                        <span>This listing is currently private</span>
                      )}
                      {((listingDetails.networks &&
                        !!listingDetails.networks.length) ||
                        listingDetails.visibility ===
                          VisibilityEnum.Public) && (
                        <Text fontSize={2} fontWeight='bold'>
                          Currently listed in
                        </Text>
                      )}

                      <ListedList>
                        {listingDetails.public_status !==
                          PublicStatusEnum.Draft && (
                          <li>
                            Agora Marketplace ({listingDetails.public_status})
                          </li>
                        )}
                        {listingDetails.networks &&
                          !!listingDetails.networks.length &&
                          getListOfNetworks({
                            networks: listingDetails.networks,
                          }).map((network) => (
                            <li key={network.id}>{network.name}</li>
                          ))}
                      </ListedList>
                    </div>
                    <Button size='small' onClick={openListingVisibilityDialog}>
                      Update
                    </Button>
                  </ActionWrapper>
                )}

                {listingDetails.visibility !== VisibilityEnum.Hidden && (
                  <EOIWrapper space={1}>
                    <Text as='h3' fontSize={4} fontWeight='medium'>
                      Expressions of Interest (EOIs)
                    </Text>
                    <EOIList listingId={listingDetails.id} />
                  </EOIWrapper>
                )}
              </>
            )}

          {isMobile && images?.length > 0 && <BuyerLogo isMobile={isMobile} />}

          <Flex padding={0} alignItems='center' gap={0}>
            <ListingTypePill type={type} />
            {!listingDetails?.org?.logo?.public_id && org?.verified_agent && (
              <VerifiedAgentTag>
                <CheckCircleIcon
                  width={30}
                  height={18}
                  color={globalTheme.colors.blue}
                />
                <Text fontSize={2}>Agent</Text>
              </VerifiedAgentTag>
            )}
            {visibility === VisibilityEnum.Private && (
              <StatusTag fontSize={2} backgroundColor='red' color='white'>
                Private
              </StatusTag>
            )}
          </Flex>
          {isMobile && visibility === VisibilityEnum.Hidden && (
            <ActionWrapper>
              <Button
                fontSize={3}
                size='medium'
                onClick={() =>
                  router.push(
                    `${PATHS.marketplace.editListing}/${listingDetails.id}?type=${listingDetails.type}`
                  )
                }
              >
                Submit
              </Button>
            </ActionWrapper>
          )}

          <Flex gap={1} alignItems='center'>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                flex: 1,
                minWidth: 0,
              }}
            >
              <CompanyDetailWrapper>
                <Text fontSize={6} fontWeight='bold'>
                  {title}
                </Text>
                <div style={{ display: 'flex' }}>
                  <Text fontSize={3}>
                    {listing_address?.suburb} {listing_address?.state}{' '}
                    {listing_address?.postcode}
                  </Text>
                </div>
                <Text fontSize={3} fontWeight={'semiBold'}>
                  {quantity_label && `${quantity_label}`}
                </Text>
                {last_updated && (
                  <Text fontSize={3}>
                    Last updated:{' '}
                    {format(new Date(last_updated), DATE_FORMAT.listing)}
                  </Text>
                )}
              </CompanyDetailWrapper>
            </div>

            {!isMobile && images?.length > 0 && (
              <BuyerLogo isMobile={isMobile} />
            )}
          </Flex>

          {isMobile && !listingDetails?.metadata?.is_org_member && (
            <ActionButtonContainer
              gap={1}
              wrap='wrap'
              showInMobile
              justifyContent='center'
            >
              <Grid gap={0} columns={'1fr 1fr'}>
                <ActionCTAButton
                  size='small'
                  variant='outline'
                  onClick={() => handleSendAction?.('enquiry')}
                >
                  Ask a question
                </ActionCTAButton>

                <ActionCTAButton
                  size='small'
                  className='send-offer-btn'
                  onClick={() => handleSendAction?.('offer')}
                >
                  Send an offer
                </ActionCTAButton>
              </Grid>
              <Grid gap={0} columns={'1fr 1fr'}>
                <ActionCTAButton
                  size='small'
                  className='request-callback-btn'
                  onClick={() => handleSendAction?.('callback')}
                >
                  Request Callback
                </ActionCTAButton>

                {visibility !== VisibilityEnum.Hidden && (
                  <ActionCTAButton
                    size='small'
                    variant='outline'
                    type='button'
                    onClick={handleShareListing}
                  >
                    <Icon size={2} component='PaperAirplaneIcon' />
                    Share
                  </ActionCTAButton>
                )}
              </Grid>
            </ActionButtonContainer>
          )}
          {/* Price Section */}
          {!sold && (
            <Divider>
              <DetailGrid
                columns='300px auto'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText>Price:</TitleText>
                <TextData>{prices_label}</TextData>
              </DetailGrid>
            </Divider>
          )}

          {sold && (
            <Divider>
              <DetailGrid
                columns='300px auto'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText>Price:</TitleText>
                <TextData>{soldPriceDetailText}</TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Enquiries category Section */}
          {form_data?.enquiries && !!form_data?.enquiries.length && (
            <Divider>
              <DetailGrid columns='300px auto' gap={0} alignItems='center'>
                <TitleText>Enquiries:</TitleText>
                <TextData>
                  {Array.isArray(form_data?.enquiries) &&
                    form_data?.enquiries
                      .map((enquiry: any) => {
                        return snakeCaseToTitleCase(enquiry);
                      })
                      .join(', ')}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Breed Section */}
          {form_data?.breed && (
            <Divider>
              <DetailGrid
                columns='300px auto'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf={'title'}>Breed:</TitleText>
                <DivWrap data-pdf='value-div'>
                  {form_data?.breed === 'Purebred' && (
                    <TextData data-pdf='value-p'>
                      {`${form_data?.purebred_breed} (purebred)`}
                    </TextData>
                  )}
                  {(form_data?.breed.includes('Crossbred') ||
                    form_data?.breed.includes('Crossbreed')) && (
                    <div data-pdf='value-p'>{`${
                      typeof form_data?.mixed_breed === 'string'
                        ? form_data?.mixed_breed
                        : form_data?.mixed_breed?.map(
                            (item: MixedBreed, index: string) => {
                              return (
                                <TextData key={index}>
                                  {item?.composition}% {item?.name}
                                </TextData>
                              );
                            }
                          )
                    }`}</div>
                  )}
                </DivWrap>
              </DetailGrid>
            </Divider>
          )}

          {/* Stock type Section */}
          {form_data?.stock_type && !!form_data?.stock_type.length && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>Stock type:</TitleText>
                <TextData data-pdf='value-p'>
                  {' '}
                  {form_data?.stock_type?.toString()}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Available From Section */}
          {date_from && (
            <Divider>
              <DetailGrid
                columns='300px auto'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf={'title'}>
                  {isAuction ? 'Auction date:' : 'Available from:'}
                </TitleText>
                <DivWrap data-pdf='value-div'>
                  {/* date_to is inclusive and always set, so equal bounds are a single date. */}
                  {formatListingDateRange(
                    date_from,
                    date_to === date_from ? null : date_to ?? null
                  )}

                  {form_data?.delivery_arrangement === 'true' && (
                    <Text fontSize={1} data-pdf='value-p'>
                      {type === ListingTypeEnum.Buy
                        ? 'Buyer can arrange for collection of livestock'
                        : 'Seller can arrange for delivery of livestock'}
                    </Text>
                  )}
                  {form_data?.delivery_at_short_notice === 'true' && (
                    <Text fontSize={1} data-pdf='value-p'>
                      {type === ListingTypeEnum.Buy
                        ? 'Can take delivery at short notice'
                        : 'Can deliver at short notice'}
                    </Text>
                  )}
                </DivWrap>
              </DetailGrid>
            </Divider>
          )}

          {/* Filled/Sold date Section — an auction's date already shows as "Auction date:" above */}
          {sold && !isAuction && (
            <Divider>
              <DetailGrid
                columns='300px auto'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf={'title'}>
                  {getListingStatusLabel(type)} Date:
                </TitleText>
                <TextData data-pdf='value-p'>
                  {' '}
                  {format(new Date(sold), DATE_FORMAT.listing)}
                </TextData>
              </DetailGrid>
            </Divider>
          )}
          {/* Stock category Section */}
          {form_data?.stock_category && !!form_data?.stock_category.length && (
            <Divider>
              <DetailGrid
                columns='300px auto'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf={'title'}>Stock Category(s):</TitleText>
                <TextData data-pdf='value-p'>
                  {Array.isArray(form_data?.stock_category) &&
                    form_data?.stock_category?.join(', ')}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Stock Gender Section */}
          {getGenderValue(product) && (
            <Divider>
              <Grid gap={1}>
                <DetailGrid
                  columns='300px 1fr'
                  gap={0}
                  alignItems='center'
                  data-pdf={'detail'}
                >
                  <TitleText data-pdf={'title'}>Stock Gender:</TitleText>
                  <TextData data-pdf='value-p'>
                    {getGenderValue(product, pregnancy_values)}
                  </TextData>
                </DetailGrid>
              </Grid>
            </Divider>
          )}

          {/* Stock Ages Section */}
          {getStockAgesValue(product) && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf={'title'}>Stock ages:</TitleText>
                <TextData data-pdf='value-p'>
                  {getStockAgesValue(product)}
                  {((form_data?.lamb_drop &&
                    Array.isArray(form_data?.lamb_drop) &&
                    !!form_data?.lamb_drop.length) ||
                    getStockWeaningDate(product)) && <> (</>}
                  {form_data?.lamb_drop &&
                    Array.isArray(form_data?.lamb_drop) &&
                    !!form_data?.lamb_drop.length && (
                      <>
                        {Array.isArray(form_data?.lamb_drop) &&
                          form_data?.lamb_drop?.join('/')}{' '}
                        drop
                      </>
                    )}
                  {getStockWeaningDate(product) && (
                    <>
                      {form_data?.lamb_drop &&
                        Array.isArray(form_data?.lamb_drop) &&
                        !!form_data?.lamb_drop.length && <>, </>}
                      <>
                        {getStockWeaningDate(product).includes('Unweaned')
                          ? getStockWeaningDate(product)
                          : getStockWeaningDate(product)}{' '}
                        {!getStockWeaningDate(product).includes('Unweaned') &&
                          `weaned`}
                      </>
                    </>
                  )}
                  {((form_data?.lamb_drop &&
                    Array.isArray(form_data?.lamb_drop) &&
                    !!form_data?.lamb_drop.length) ||
                    getStockWeaningDate(product)) && <>)</>}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Tags Section */}
          {form_data?.tags && !!form_data?.tags.length && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf={'title'}>Tags:</TitleText>
                <TextData data-pdf='value-p'>
                  {Array.isArray(form_data?.tags) &&
                    form_data?.tags?.join(', ')}
                </TextData>
              </DetailGrid>
            </Divider>
          )}
          {type === ListingTypeEnum.Buy && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf={'title'}>Preferred Weight:</TitleText>
                <ExpectedWeight
                  weightMin={form_data?.weight_expected_min}
                  weightMax={form_data?.weight_expected_max}
                  weightAvg={form_data?.weight_expected_avg}
                />
              </DetailGrid>
            </Divider>
          )}

          {/* Weight Section */}
          {form_data?.weighed === 'true' &&
            (form_data?.weight_range_min ||
              form_data?.weight_range_avg ||
              form_data?.weight_range_max) && (
              <Divider>
                <Grid gap={1}>
                  {form_data?.last_weighed && (
                    <DetailGrid
                      columns='300px 1fr'
                      gap={0}
                      alignItems='center'
                      data-pdf={'detail'}
                    >
                      <TitleText data-pdf={'title'}>Last weighed:</TitleText>
                      <DivWrap data-pdf='value-div'>
                        <TextData>
                          {format(
                            Date.parse(form_data?.last_weighed),
                            DATE_FORMAT.listing
                          )}
                        </TextData>
                        <TextData data-pdf='value-p'>
                          <ExpectedWeight
                            weightMin={form_data?.weight_range_min}
                            weightAvg={form_data.weight_range_avg}
                            weightMax={form_data?.weight_range_max}
                          />
                        </TextData>
                      </DivWrap>
                    </DetailGrid>
                  )}
                </Grid>
              </Divider>
            )}
          {/*Estimate weight at delivery*/}
          {(form_data?.weight_expected_min ||
            form_data?.weight_expected_avg ||
            form_data?.weight_expected_max) && (
            <Divider>
              <Grid gap={1}>
                <DetailGrid
                  columns='300px 1fr'
                  gap={0}
                  alignItems='center'
                  data-pdf={'detail'}
                >
                  <TitleText data-pdf={'title'}>
                    Expected weight at delivery:
                  </TitleText>
                  <DivWrap data-pdf='value-div'>
                    <ExpectedWeight
                      weightMin={form_data?.weight_expected_min}
                      weightAvg={form_data?.weight_expected_avg}
                      weightMax={form_data?.weight_expected_max}
                    />
                  </DivWrap>
                </DetailGrid>
              </Grid>
            </Divider>
          )}

          {form_data?.weighed === 'false' && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf={'title'}>Last Weighed:</TitleText>
                <TextData data-pdf='value-p'>Unweighed</TextData>
              </DetailGrid>
            </Divider>
          )}
          {/* Expected Fat */}
          {form_data?.expected_fat && !!form_data?.expected_fat.length && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf={'title'}>
                  Expected fat/condition(s) score at delivery:
                </TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.expected_fat.join(', ')}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* wool fibre */}
          {form_data?.wool_fibre_length &&
            !!form_data?.wool_fibre_length.length && (
              <Divider>
                <DetailGrid
                  columns='300px 1fr'
                  gap={0}
                  alignItems='center'
                  data-pdf={'detail'}
                >
                  <TitleText data-pdf={'title'}>Last shorn:</TitleText>
                  <TextData data-pdf='value-p'>
                    {Array.isArray(form_data?.wool_fibre_length)
                      ? form_data?.wool_fibre_length.join('/')
                      : form_data?.wool_fibre_length}
                  </TextData>
                </DetailGrid>
              </Divider>
            )}

          {/* Feed Type Section */}
          {form_data?.feed_type && !!form_data?.feed_type.length && (
            <Divider>
              <Grid gap={1}>
                <DetailGrid
                  columns='300px 1fr'
                  gap={0}
                  alignItems='center'
                  data-pdf={'detail'}
                >
                  <TitleText data-pdf={'title'}>Feed type:</TitleText>
                  <TextData data-pdf='value-p'>
                    {Array.isArray(form_data?.feed_type) &&
                      form_data?.feed_type?.join(', ')}
                  </TextData>
                </DetailGrid>
                {form_data?.num_days_grain && (
                  <DetailGrid
                    columns='300px 1fr'
                    gap={0}
                    alignItems='center'
                    data-pdf={'detail'}
                  >
                    <TitleText data-pdf={'title'}>
                      Days on grain/pellets:
                    </TitleText>
                    <TextData data-pdf='value-p'>
                      {form_data?.num_days_grain}
                    </TextData>
                  </DetailGrid>
                )}
              </Grid>
            </Divider>
          )}

          {/* Dentition Section */}
          {form_data?.dentition?.length > 0 && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf={'title'}>Dentition:</TitleText>
                <TextData data-pdf='value-p'>
                  {Array.isArray(form_data?.dentition) &&
                    form_data?.dentition?.join(', ')}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Treatments Section */}
          {form_data?.treatments === 'true' && (
            <Divider>
              <DetailGrid
                columns={'300px 1fr'}
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf={'title'}>
                  Animal health treatments:
                </TitleText>
                <Grid rows={'1fr 1frs'} data-pdf='value-p'>
                  <div data-pdf='sub-value'>
                    {typeof form_data?.treatment_type === 'string'
                      ? form_data?.treatment_type
                      : form_data?.treatment_type?.map(
                          (item: TreatmentType, index: string) => {
                            return (
                              <>
                                {item.name && (
                                  <TextData key={index}>
                                    {item?.name}
                                    {item.treatment_date &&
                                      item.treatment_date !== '' &&
                                      ` - ${format(
                                        Date.parse(item?.treatment_date),
                                        DATE_FORMAT.listing
                                      )}`}
                                  </TextData>
                                )}
                              </>
                            );
                          }
                        )}
                  </div>
                  {form_data?.withholding && (
                    <TextData data-pdf='sub-value'>
                      {form_data?.withholding === 'true'
                        ? `Withholding until ${
                            form_data?.withholding_finish_date
                              ? `${format(
                                  Date.parse(
                                    form_data?.withholding_finish_date
                                  ),
                                  DATE_FORMAT.listing
                                )}`
                              : ''
                          }`
                        : 'Not in withholding'}
                    </TextData>
                  )}
                </Grid>
              </DetailGrid>
            </Divider>
          )}

          {/* HGP Free */}
          {form_data?.hgp_free && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>HGP free:</TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.hgp_free === 'true' ? 'Yes' : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}
          {/* Lifetime traceable */}
          {form_data?.lifetime_traceable && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>Lifetime traceable:</TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.lifetime_traceable === 'true' ? 'Yes' : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}
          {/* Vendor bred? (no pink tags) */}
          {form_data?.vendor_bred && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>Vendor bred:</TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.vendor_bred === 'true' ? 'Yes' : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Lice Section */}
          {form_data?.lice && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>Have they got lice:</TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.lice === 'true' ? 'Yes' : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Footrot */}
          {form_data?.footrot && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>Have they got footrot:</TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.footrot === 'true' ? 'Yes' : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Blue Tongue Free */}
          {form_data?.blue_tongue_free && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>
                  From Blue Tongue free zone:
                </TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.blue_tongue_free === 'true' ? 'Yes' : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* LPA accredited */}
          {form_data?.lpa_accredited && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>LPA accredited:</TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.lpa_accredited === 'true' ? 'Yes' : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Mulesed*/}
          {form_data?.mulesed && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>Mulesed:</TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.mulesed === 'true' ? 'Yes' : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Antibiotic free*/}
          {form_data?.antibiotic_free && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>Antibiotic free: </TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.antibiotic_free === 'true' ? 'Yes' : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Organic */}
          {form_data?.organic && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>Organic:</TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.organic === 'true' ? 'Yes' : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Blemishes*/}
          {form_data?.blemishes && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>
                  Do they have any blemishes?
                </TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.blemishes === 'true'
                    ? `Yes - ${form_data?.blemishes_detail}`
                    : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Will they be fit to load */}
          {form_data?.fit_to_load && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>
                  Will they be fit to load:
                </TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.fit_to_load === 'true' ? 'Yes' : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Tick free zone */}
          {form_data?.tick_free_zone && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>
                  Are they from a Tick free zone:
                </TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.tick_free_zone === 'true' ? 'Yes' : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Horns Section */}
          {form_data?.horns && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>Horns:</TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.horns === 'true'
                    ? 'Yes - ' +
                      (Array.isArray(form_data?.horn_type)
                        ? form_data?.horn_type.join(', ')
                        : form_data?.horn_type) +
                      `${
                        form_data?.horn_length
                          ? '(' + form_data?.horn_length + 'cm' + ')'
                          : ''
                      } `
                    : 'No'}
                </TextData>
              </DetailGrid>
            </Divider>
          )}

          {/* Agents Section */}
          {getAgents() && !org?.verified_agent && (
            <>
              <Divider>
                <DetailGrid
                  columns='300px 1fr'
                  gap={0}
                  alignItems='center'
                  data-pdf={'detail'}
                >
                  <TitleText data-pdf='title'>Agent:</TitleText>
                  <div data-pdf='value-p'>{getAgents()}</div>
                </DetailGrid>
              </Divider>
            </>
          )}

          {/* Comments Section */}
          {form_data?.comments && (
            <Divider>
              <DetailGrid
                columns='300px 1fr'
                gap={0}
                alignItems='center'
                data-pdf={'detail'}
              >
                <TitleText data-pdf='title'>Comments:</TitleText>
                <TextData data-pdf='value-p'>
                  {form_data?.comments
                    .split('\n')
                    .map((text: string, idx: number) => (
                      <p key={idx}>
                        <Linkify
                          options={{
                            target: '_blank',
                            rel: 'noopener noreferrer',
                            className: 'comment-link-text',
                          }}
                        >
                          {text}
                        </Linkify>
                      </p>
                    ))}
                </TextData>
              </DetailGrid>
            </Divider>
          )}
          {files.length > 0 && (
            <>
              <Text
                fontSize={4}
                fontWeight='bold'
                style={{
                  marginBottom: globalTheme.space[0],
                  marginTop: globalTheme.space[1],
                }}
              >
                Files
              </Text>
              <StyledGrid>
                {files.map((file) => {
                  const pdfMedia = cld
                    .image(file.public_id)
                    .format('pdf')
                    .toURL();

                  return (
                    <FileChip
                      key={file.id}
                      id={file.id}
                      name={file.name}
                      urls={pdfMedia}
                    />
                  );
                })}
              </StyledGrid>
            </>
          )}
        </HeroContentDescription>
        <div>
          {images?.length === 0 && (
            <ImageContainer>
              {org?.logo ? (
                <img
                  {...buildResponsiveImage(
                    (width) =>
                      cld
                        .image((org.logo as ListingMedia).public_id)
                        .format('jpg')
                        .quality('auto')
                        .resize(
                          pad()
                            .aspectRatio('3:2')
                            .width(width)
                            .background(Background.color(Color.WHITE))
                        ),
                    CARD_IMAGE_WIDTHS
                  )}
                  sizes={DETAIL_MEDIA_SIZES}
                  alt={`${org?.name || 'Seller'} logo`}
                />
              ) : (
                getPlaceholderImage(product, type, 'responsive')
              )}
            </ImageContainer>
          )}

          {media?.length > 0 && (
            <FullScreenImageViewer images={images} altText={imageAltText} />
          )}

          <ActionButtonContainer gap={2} wrap='wrap' justifyContent='center'>
            {!listingDetails?.metadata?.is_org_member && (
              <Flex
                justifyContent='center'
                gap={2}
                stackOnSmall
                alignItems='center'
              >
                <ActionCTAButton
                  size='small'
                  variant='outline'
                  onClick={() => handleSendAction?.('enquiry')}
                >
                  Ask a question
                </ActionCTAButton>

                <ActionCTAButton
                  size='small'
                  className='send-offer-btn'
                  onClick={() => handleSendAction?.('offer')}
                >
                  Send an offer
                </ActionCTAButton>

                <ActionCTAButton
                  size='small'
                  className='request-callback-btn'
                  onClick={() => handleSendAction?.('callback')}
                >
                  Request Callback
                </ActionCTAButton>
              </Flex>
            )}

            {visibility !== VisibilityEnum.Hidden && (
              <ActionCTAButton
                size='small'
                variant='outline'
                type='button'
                onClick={handleShareListing}
              >
                <Icon size={2} component='PaperAirplaneIcon' />
                Share this listing!
              </ActionCTAButton>
            )}
          </ActionButtonContainer>
        </div>
      </HeroContent>

      <Box style={{ padding: '20px 0' }}>
        {/* Analytics Section */}
        {(listingDetails?.metadata?.is_owner ||
          listingDetails?.metadata?.is_org_member) && (
          <ListingAnalyticsInfo
            listingDetails={listingDetails || publicListingData}
          />
        )}
      </Box>

      <UpdateListingVisibilityDialog
        isOpen={isListingVisibilityOpen}
        onClose={closeListingVisibilityDialog}
        listingDetails={listingDetails}
      />
    </>
  );
}

export default ListingDetails;
